import { FC, memo, useCallback, useContext, useEffect, useLayoutEffect, useReducer, useState } from "react";
import PaginationNav from "src/components/actionDashboard/paginationNav";
import NoDataFound from "src/components/noDataFoundComponent";
import { QueryParamsState } from "src/components/manufacturer/caseListing";
import CopayCompletedClaimsHeader from "src/components/copayClaims/completedClaimsTableHeader";
import CompletedClaimsTableRow from "src/components/copayClaims/completedClaimsTableRow";
import { ClaimQueryParamsState, ClaimQueryStateActions, ClaimQueryStateActionType, CompletedClaimsResponseType, CopayCompletedClaimsType, CopayCompletedClaimsTypeProps } from "./types";
import { APIEndpoints } from "src/types/apiTypes";
import useFetch from "src/hooks/useFetch";
import { LoaderContext } from "src/context/loaderContext";
import { claimInitialState, ClaimQueryParamReducer } from "./reducer";
import CopayFilter from "src/components/copayFilter";
import { downloadExcelFile } from "src/helpers/downloadFile";
import { format, parse } from "date-fns";
import { ToastTypes } from "src/types";
import { ToastContext } from "src/context/toastContext";


const CompletedClaims: FC<CopayCompletedClaimsTypeProps> = ({ initialState, refreshListing, activeTab }): JSX.Element => {


  const [sortedData, setSortedData] = useState<CopayCompletedClaimsType[]>([]);
  const [maxRecordData, setMaxRecordsData] = useState<number>(0);
  const [queryParams, dispatch] = useReducer(ClaimQueryParamReducer, claimInitialState);
  const { setLoader, hideLoader } = useContext(LoaderContext);
  const { showToast } = useContext(ToastContext);
  const [isExportingInProgress, setExportingInProgressStatus] = useState<boolean>(false);
  const [isPaginationUpdated, updatePaginationChange] = useState<boolean>(false)
  const [Queries, setUpdatedQueries] = useState<ClaimQueryParamsState>(queryParams);
  const [currentPage, setCurrentPage] = useState<number>(1);


  const { data: completedClaimsData, error: completedClaimsError, mutate: completedClaimsUpdateData } =
    useFetch<CompletedClaimsResponseType>(APIEndpoints.COMPLETED_CLAIMS, queryParams, {}, true);

  useEffect(() => {
    if (completedClaimsData || completedClaimsError) {
      hideLoader()

      if (completedClaimsData) {
        if (queryParams.pagination) {
          setSortedData(completedClaimsData.records);
          setMaxRecordsData(completedClaimsData.max_page)

        } else if (completedClaimsData.response && completedClaimsData.response.length > 0) {
          dispatch({
            type: ClaimQueryStateActions.CHANGE_PAGINATION_BIT,
            payload: true,
          })
          downloadExcelFile(completedClaimsData.response, `${completedClaimsData.report_name}.xlsx`);
          setExportingInProgressStatus(false)
        } else {
          setExportingInProgressStatus(false)
          dispatch({
            type: ClaimQueryStateActions.CHANGE_PAGINATION_BIT,
            payload: true,
          })
          showToast(['Report cannot be generated'], ToastTypes.ERROR);
        }

      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedClaimsData, completedClaimsError]);


  const [queryParam, setQueryParam] = useState<QueryParamsState>({
    direction: "desc",
    sort_by: "case_id",
  });


  const changeIdOrder = (tableHeader: string) => {
    const newParams: QueryParamsState = {
      sort_by: tableHeader,
      direction:
        tableHeader === queryParam.sort_by
          ? queryParam.direction === "desc"
            ? "asc"
            : "desc"
          : "desc",
    };

    let sorted = [...(sortedData || [])];

    switch (newParams.sort_by) {
      case "case_id":
        sorted.sort((a, b) =>
          newParams.direction === "asc"
            ? Number(a.case_id) - Number(b.case_id)
            : Number(b.case_id) - Number(a.case_id)
        );
        break;

      case "submitting_practice":
        sorted.sort((a, b) =>
          newParams.direction === "desc"
            ? (a.submitting_practice || "").localeCompare(b.submitting_practice || "")
            : (b.submitting_practice || "").localeCompare(a.submitting_practice || "")
        );
        break;

      case "patient_name":
        sorted.sort((a, b) =>
          newParams.direction === "desc"
            ? (a.patient_name || "").localeCompare(b.patient_name || "")
            : (b.patient_name || "").localeCompare(a.patient_name || "")
        );
        break;

      case "claim_status":
        sorted.sort((a, b) =>
          newParams.direction === "desc"
            ? (a.claim_status || "").localeCompare(b.claim_status || "")
            : (b.claim_status || "").localeCompare(a.claim_status || "")
        );
        break;
        case "paid_claim_date":
        sorted.sort((a, b) =>
          newParams.direction === "desc"
            ? (a.paid_claim_date || "").localeCompare(b.paid_claim_date || "")
            : (b.paid_claim_date || "").localeCompare(a.paid_claim_date || "")
        );
        break;
        case "paid_claim_amount":
        sorted.sort((a, b) =>
          newParams.direction === "desc"
            ? (a.paid_claim_amount || "").localeCompare(b.paid_claim_amount || "")
            : (b.paid_claim_amount || "").localeCompare(a.paid_claim_amount || "")
        );
        break;
        //remaining_copay_available
        case "remaining_copay_available":
        sorted.sort((a, b) =>
          newParams.direction === "desc"
            ? Number(a.remaining_copay_available) - Number(b.remaining_copay_available)
            : Number(b.remaining_copay_available) - Number(a.remaining_copay_available)
        );
        break;

        case "denial_reason":
        sorted.sort((a, b) =>
          newParams.direction === "desc"
            ? (a.denial_reason || "").localeCompare(b.denial_reason || "")
            : (b.denial_reason || "").localeCompare(a.denial_reason || "")
        );
        break;
      default:
        break;
    }
    setSortedData(sorted);
    setQueryParam(newParams);
  };


  //Show loader initially
  useLayoutEffect(() => {
    setLoader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);





  const onClearButtonClicked = () => {
    setLoader()
    dispatch({
      type: ClaimQueryStateActions.CLEAR_STATE,
      payload: true
    });
    setCurrentPage(1)
    setUpdatedQueries({ ...queryParams, claim_status: '', case_id: '', start_date: '', end_date: '', patient_name: '', file_required: false ,page: 1})
    completedClaimsUpdateData({}, {}, { ...queryParams, claim_status: '', case_id: '', start_date: '', end_date: '', patient_name: '', file_required: false ,page: 1})
  };

  //👇 jab data load ho jaye tab default tab set karo


  const onPageChanged = (pageObj: { selected: number }) => {
    const action: ClaimQueryStateActionType = {
      type: ClaimQueryStateActions.CHANGE_PAGE,
      payload: pageObj.selected + 1,
    };
    setCurrentPage(pageObj.selected + 1)
    updatePaginationChange(true)
    const tempParams = {
      ...queryParams,
    };
    const newState = ClaimQueryParamReducer(tempParams, action);
    dispatch(action);
    //setLoader();

    dispatch(action);
    updatePaginationChange(true)
    completedClaimsUpdateData({}, {}, { ...newState })

  };


  const handlePageChange = useCallback(
    (pageObj: { selected: number }) => onPageChanged(pageObj),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryParams]
  );

  const updateQueryParamsAndCallAPI = (updatedState: ClaimQueryParamsState): void => {
    console.log("updatedState...", updatedState, " QueryParams...")
    const queryParams = {
      ...updatedState,
      start_date:updatedState.start_date && (format(parse(updatedState.start_date, "MM/dd/yyyy", new Date()), "yyyy-MM-dd")),
      end_date:updatedState.end_date && (format(parse(updatedState.end_date, "MM/dd/yyyy", new Date()), "yyyy-MM-dd"))

    };
    dispatch({
      type: ClaimQueryStateActions.CHANGE_SORT_PARAMS,
      payload: queryParams,
    });
    setLoader();
    setCurrentPage(1)
    completedClaimsUpdateData({}, {}, { ...queryParams, pagination: false, file_required: false, page:1 })
  };

  const downloadExcel = () => {
    setExportingInProgressStatus(true)
    dispatch({
      type: ClaimQueryStateActions.CHANGE_PAGINATION_BIT,
      payload: false,
    })
    //setLoader();
    completedClaimsUpdateData({}, {}, { ...queryParams, pagination: false, file_required: true })
  }

  return (
    <>
      <div className="row my-4">
        <div className="col-12 col-lg-9">
          <CopayFilter
            state={queryParams}
            dispatch={dispatch}
            handleSearchWithQueryParam={updateQueryParamsAndCallAPI}
            onClearButtonClicked={onClearButtonClicked}
            activeTabName={activeTab}
          />
        </div>
        <div className="col-12 col-lg-3 text-end">
          {isExportingInProgress ? (
            <div className="col">
              <p className="text-dark h5">
                <span className="spinner-border spinner-border-sm"
                  style={{ width: 20, height: 20 }} aria-hidden="true"></span>{' '}
                <i>Fetching Co-Pay Claim Report</i>
              </p>
            </div>
          ) : (
            <button type="button" className="btn btn-primary" onClick={downloadExcel}>
              Co-Pay Claim Report
            </button>
          )}
        </div>
      </div>
      <div className="card mb-3 d-flex flex-fill">
        <div className="card-body p-0 d-flex flex-column">
          <div className="table-responsive-sm table-responsive-md table-responsive-lg flex-fill">
            <table className="table table-striped table-sm m-0">
              <CopayCompletedClaimsHeader queryParam={queryParam}
                                          changeIdOrder={changeIdOrder} />
              <tbody>
              {sortedData && sortedData.length > 0 ?
                  sortedData.map((data: CopayCompletedClaimsType, index: number) => (
                      <CompletedClaimsTableRow refreshListing={refreshListing} treatable={data} key={index} />
                  ))
                  : <div className="pt-4">
                    <NoDataFound />
                  </div>
              }
              </tbody>
            </table>
          </div>
          {maxRecordData && maxRecordData > 1 ?
            <PaginationNav currentPage={currentPage} pageCount={maxRecordData} onPageChange={handlePageChange} /> : null
          }
        </div>
      </div>

    </>
  );
};

export default memo(CompletedClaims);
